import { Button, Stack, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useLangContext } from "../../../context/langContext";
import compInfos from "./sidebar-menu.json";
import { useThemeContext } from "../../../context/themeProvider";
import { routerPaths } from "../../../constants/Paths";
import { useAuthContext } from "../../../context/authContext";
import {
  CallCalling,
  CallOutgoing,
  Cloud,
  Coin,
  Coin1,
  EmptyWalletAdd,
  FolderAdd,
  GridLock,
  House,
  LockCircle,
  LogoutCurve,
  People,
  Profile2User,
  Receipt21,
  Ticket2,
  Unlock,
  VoiceCricle,
  ShieldSecurity,
  DocumentText,
  Call,
  DocumentCopy,
  CallSlash,
  CallReceived,
} from "iconsax-react";

import { useCanAccess } from "../../../hooks/canAccess";
import { useUrl } from "../../../hooks/urlEncoding";
import { AccessEnums } from "../../../constants/accessEnum";
import { SortDirectionEnum } from "../../../services/Schemas/ListQueryString";
import { TicketListColumnSortable } from "../../../services/Endpoints/tickets/TicketListEndpoints";
import { BlockNumbersListColumnSortable } from "../../../services/Endpoints/block-numbers/BlockNumbersListEndpoint";
import { SendCallsListColumnSortable } from "../../../services/Endpoints/send-calls/SendCallsEndpoint";

const settingButtons = [
  {
    id: "language",
    icon: "/images/language.svg",
    name: "Language",
  },
  {
    id: "theme",
    icon: "/images/theme.svg",
    name: "Theme",
  },
  {
    id: "logout",
    icon: "/images/logout.svg",
    name: "Logout",
  },
];

interface ISidebarMenuProps {
  setDrawerOpen: (payload: boolean) => void;
}

const handleActiveLink = (link: string, pathname: string) => {
  const linkPath = link.split("?")[0];
  const path = pathname.split("?")[0];

  if (linkPath === "/") return path === "/";
  return path === linkPath || path.startsWith(linkPath + "/");
};

export default function SidebarMenuComponent({
  setDrawerOpen,
}: Readonly<ISidebarMenuProps>) {
  const { lang, dir, toggleLang } = useLangContext();
  const { toggleTheme, mode } = useThemeContext();
  const { logout } = useAuthContext();
  const { can } = useCanAccess();
  const { queryStringUrl } = useUrl();
  const subMenuItems = [
    {
      id: "report",
      Icon: DocumentCopy,
      link: queryStringUrl(routerPaths.REPORT, {}),
      key: AccessEnums.AdminReportsCallQueueSender,
    },
    {
      id: "voiceBroadcasting",
      Icon: VoiceCricle,
      name: "Mass call",
      link: queryStringUrl(routerPaths.VOICE_BROADCASTS, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminVoiceBroadcastsList,
    },
    {
      id: "numberDetail",
      Icon: CallOutgoing,
      link: queryStringUrl(routerPaths.BROAD_NUMBER, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminVoiceBroadcastNumbersList,
    },
    {
      id: "sendCalls",
      Icon: CallReceived,
      link: queryStringUrl(routerPaths.SEND_CALLS, {
        sorts: {
          [SendCallsListColumnSortable.ID]: {
            column: SendCallsListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminSecureCallsList,
    },
    {
      id: "myFiles",
      Icon: FolderAdd,
      link: queryStringUrl(routerPaths.MY_FILES, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminFilesList,
    },

    {
      id: "pbxCallDetail",
      Icon: CallCalling,
      link: queryStringUrl(routerPaths.PBX_CALL_DETAIL, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminPbxCallDetailRecordsList,
    },
    {
      id: "partners",
      Icon: People,
      link: queryStringUrl(routerPaths.PARTNERS, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminPartnerList,
    },
    {
      id: "block-numbers",
      Icon: CallSlash,
      link: queryStringUrl(routerPaths.BLOCK_NUMBERS, {
        sorts: {
          [BlockNumbersListColumnSortable.ID]: {
            column: BlockNumbersListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminBlockNumbersList,
    },
    {
      id: "servers",
      Icon: Cloud,
      link: queryStringUrl(routerPaths.SERVERS, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminPbxServersList,
    },

    {
      id: "accesses",
      Icon: GridLock,
      link: queryStringUrl(routerPaths.ACCESS, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminAclSectionsList,
    },
    {
      id: "accessToken",
      Icon: LockCircle,
      link: queryStringUrl(routerPaths.ACCESS_TOKEN, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminAccessTokensList,
    },
    {
      id: "roles",
      Icon: Unlock,
      link: queryStringUrl(routerPaths.ROLES, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminAclRolesList,
    },
    {
      id: "users",
      Icon: Profile2User,
      link: queryStringUrl(routerPaths.USERS, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminUsersList,
    },
    {
      id: "receipts",
      Icon: Receipt21,
      link: queryStringUrl(routerPaths.RECEIPTS, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminPaymentReceiptsList,
    },
    {
      id: "payments",
      Icon: Coin,
      link: queryStringUrl(routerPaths.PAYMENTS, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminPaymentGatewayList,
    },
    {
      id: "workspaces",
      Icon: House,
      link: queryStringUrl(routerPaths.WORKSPACES, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminWorkspacesList,
    },
    {
      id: "support",
      Icon: Ticket2,
      link: queryStringUrl(routerPaths.SUPPORT, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminTicketsList,
    },
    {
      id: "secure-calls",
      Icon: ShieldSecurity,
      link: queryStringUrl(routerPaths.SECURE_CALLS, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminSecureCallsList,
    },
    {
      id: "otp-calls",
      Icon: Call,
      link: queryStringUrl(routerPaths.OTP_CALLS, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminOTPCallsList,
    },
    {
      id: "logs",
      Icon: DocumentText,
      link: queryStringUrl(routerPaths.LOGS, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminLogsList,
    },
    {
      id: "wallet",
      Icon: EmptyWalletAdd,
      link: queryStringUrl(routerPaths.WALLET, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminWalletsList,
    },
    {
      id: "wallet_transaction",
      Icon: Coin1,
      link: queryStringUrl(routerPaths.WALLET_TRANSACTION, {
        sorts: {
          [TicketListColumnSortable.ID]: {
            column: TicketListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        },
      }),
      key: AccessEnums.AdminTransactionsList,
    },
  ];

  const settingsFunctionCalls = {
    language: toggleLang,
    theme: toggleTheme,
    logout: () => {
      logout();
      window.location.replace("/login");
    },
  };

  const handleSettingCalls = (id: "language" | "theme" | "logout") => {
    setDrawerOpen(false);
    settingsFunctionCalls[id]();
  };

  const infos = compInfos[lang];
  const menuItems = subMenuItems.map(({ Icon, link, id, key }) => ({
    link,
    Icon,
    name: infos.controllerButtons[id as "support"],
    key,
  }));
  const location = useLocation();
  return (
    <Stack
      direction="column"
      justifyContent="space-between"
      sx={{overflowY:'auto'}}
      maxHeight={{ xs: "100vh", md: "75vh" }}
    >
      <Stack
        direction="column"
        justifyContent="start"
        alignItems="start"
        py={{ xs: "90px", md: "0px" }}
      >
        {menuItems
          .filter((itm) => can(itm.key))
          .map(({ Icon, link, name }) => (
            <Stack
              component={Link}
              to={link}
              sx={{
                textDecoration: "none",
                whiteSpace: "nowrap",
                justifyContent: "start",
                width: "100%",
              }}
            >
              <Button
                dir={dir}
                key={name}
                fullWidth
                onClick={() => {
                  setDrawerOpen(false);
                }}
                sx={{
                  backgroundColor: handleActiveLink(link, location.pathname)
                    ? ({ palette }) => palette.gray[100]
                    : "transparent",
                  color: handleActiveLink(link, location.pathname)
                    ? ({ palette }) => palette.dominant.main
                    : ({ palette }) => palette.dark[40],
                  border: "none",
                  borderRadius: "0",
                  py: "8px",
                  "&:hover": {
                    backgroundColor: ({ palette }) => palette.gray[100],
                    "&:focus": {
                      outline: "none",
                    },
                  },
                  borderRightWidth: "4px",
                  borderRightStyle: "solid",
                  borderRightColor: (thm) =>
                    handleActiveLink(link, location.pathname)
                      ? thm.palette.accent.main
                      : "transparent",
                }}
              >
                <Stack
                  width="100%"
                  mr="auto"
                  direction="row"
                  justifyContent="start"
                  alignItems="center"
                  gap="14px"
                  px="10px"
                >
                  <Icon />
                  <Typography variant="footerMediumBold">{name}</Typography>
                </Stack>
              </Button>
            </Stack>
          ))}
      </Stack>
      <Stack
        width="100%"
        direction="column"
        justifyContent="start"
        alignItems="start"
        py="26px"
        mt="auto"
      >
        {settingButtons.map(({ icon, id, name }) => (
          <Button
            dir={dir}
            key={id}
            fullWidth
            onClick={() => handleSettingCalls(id as "language")}
            sx={{
              display: id == "logout" ? "block" : "none",
              backgroundColor: "transparent",
              border: "none",
              borderRadius: "0",
              py: "8px",
              color: ({ palette }) => palette.dark[40],
              "&:hover": {
                backgroundColor: ({ palette }) => palette.gray[100],
                "&:focus": {
                  outline: "none",
                },
              },
            }}
          >
            <Stack
              width="96%"
              mr="auto"
              direction="row"
              justifyContent="start"
              alignItems="center"
              gap="14px"
              px="10px"
            >
              <Stack sx={{ rotate: "180deg" }}>
                <LogoutCurve size={22} />
              </Stack>
              <Typography
                variant="h6MediumBold"
                color={({ palette }) => palette.dark[40]}
              >
                {infos.settingButtons[id as "language"]}{" "}
                {(infos.settingButtons[id as "language"] == "theme" ||
                  infos.settingButtons[id as "language"] == "تم") &&
                  infos.theme[mode]}
              </Typography>
            </Stack>
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}
